import { type Dispatch } from '@reduxjs/toolkit';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { getHead } from '../../head';
import { type ILegalState } from '../reducers/legal/types';
import { setActiveCategory } from '../reducers/products/reducer';

function CrossedOutBinSymbol({ title }: { title: string }): React.JSX.Element {
  return (
    <svg
      className="take-back--symbol"
      viewBox="0 0 64 80"
      width="64"
      height="80"
      role="img"
      aria-labelledby="take-back-symbol-title"
    >
      <title id="take-back-symbol-title">{title}</title>
      <g
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinejoin="round"
      >
        <path d="M16 18h32l-4 40H20z" />
        <path d="M12 18h40" />
        <path d="M26 12h12v6H26z" />
        <path d="M24 26v26M32 26v26M40 26v26" />
        <circle cx="22" cy="62" r="4" />
        <circle cx="42" cy="62" r="4" />
        <path d="M6 6l52 62M58 6L6 68" strokeLinecap="round" />
      </g>
      <rect x="10" y="72" width="44" height="6" fill="currentColor" />
    </svg>
  );
}

function TakeBackContainer({
  dispatch,
  Legal,
}: {
  dispatch: Dispatch;
  Legal: ILegalState;
}): React.JSX.Element {
  const { t } = useTranslation();
  const seller = Legal.seller;
  const email: string = seller?.email ?? '';

  useEffect((): void => {
    dispatch(setActiveCategory({ category: { uuid: '' } }));
  }, [dispatch]);

  useEffect((): void => {
    const { title } = getHead(t, t('takeBack.title'));
    document.title = title;
  }, [t]);

  return (
    <div className="container container--t terms take-back">
      <h1>{t('takeBack.title')}</h1>

      <section aria-labelledby="take-back-symbol">
        <h2 id="take-back-symbol">{t('takeBack.symbolHeading')}</h2>
        <CrossedOutBinSymbol title={t('takeBack.symbolAlt')} />
        <p>{t('takeBack.symbolText')}</p>
      </section>

      <section aria-labelledby="take-back-ban">
        <h2 id="take-back-ban">{t('takeBack.banHeading')}</h2>
        <p>{t('takeBack.banText')}</p>
      </section>

      <section aria-labelledby="take-back-free">
        <h2 id="take-back-free">{t('takeBack.freeHeading')}</h2>
        <p>{t('takeBack.freeEquipmentText')}</p>
        <p>{t('takeBack.freeBatteriesText')}</p>
      </section>

      <section aria-labelledby="take-back-request">
        <h2 id="take-back-request">{t('takeBack.requestHeading')}</h2>
        <p>{t('takeBack.placeText')}</p>
        {seller ? (
          <address>
            <p>{seller.companyName}</p>
            <p>{seller.street}</p>
            <p>{`${seller.zip} ${seller.city}`}</p>
            <p>{seller.country}</p>
          </address>
        ) : null}
        <p>
          {t('takeBack.requestText')}{' '}
          {email ? <a href={`mailto:${email}`}>{email}</a> : null}
        </p>
        <p>{t('takeBack.requestInstructions')}</p>
      </section>

      <section aria-labelledby="take-back-collection">
        <h2 id="take-back-collection">{t('takeBack.collectionHeading')}</h2>
        <p>{t('takeBack.collectionText')}</p>
      </section>

      <section aria-labelledby="take-back-why">
        <h2 id="take-back-why">{t('takeBack.whyHeading')}</h2>
        <p>{t('takeBack.whyText')}</p>
      </section>
    </div>
  );
}

const mapStateToProps = ({ Legal }: { Legal: ILegalState }) => ({ Legal });

export default connect(mapStateToProps)(TakeBackContainer);
